import { useMemo } from 'react';
import { Box, Column, SimpleLabel, Svg, SvgBubble, Tooltip } from '@hopskipdrive/react-components';
import { Tooltip as MuiTooltip } from '@mui/material';
import { Text } from '@rideiq/shared/components/Text';
import { includes, uniqueId } from 'lodash';
import styled from 'styled-components';

import { RIDE_STATES, RIDE_STATES_QUERY } from '../constants';
import { useTimezone } from '../hooks/useTimezone';
import { RiderServices, SERVICE_ICON_CODES } from '../riders/features/RiderServices';
import { OverflowText } from '../shared/components';
import { colorPalette, colorTokens } from '../shared/components/variables';
import { formatCurrency, getFirstNameLastInitial } from '../utils/helpers';

export const useRidesTableColumns = (state: string) => {
  const { formatInTimezone } = useTimezone();

  const columns = useMemo(() => {
    if (state === RIDE_STATES_QUERY.upcoming || state === RIDE_STATES_QUERY.requested) {
      return upcomingRides(formatInTimezone);
    } else if (state === RIDE_STATES_QUERY.completed) {
      return completedRides(formatInTimezone);
    } else if (state === RIDE_STATES_QUERY.billableCanceled || state === RIDE_STATES_QUERY.allCanceled) {
      return canceledRides(formatInTimezone);
    }

    return [];
  }, [state]);

  return columns as Column[];
};

const upcomingRides = (formatInTimezone) => {
  return [
    statusColumn,
    passengersColumn(),
    {
      key: 'startsAt',
      label: 'Planned Pickup Time',
      align: 'left',
      valueSetter: (cellValue) => cellValue && formatInTimezone(cellValue, 'hh:mm aa'),
      minWidth: '167px',
    },

    {
      label: (
        <Box style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Text textColor={colorPalette.neutralVariant40} variant="bodyS" fontWeight={300}>
            CareDriver Arrival
          </Text>
          <Tooltip title="Based on CareDriver app activity, this is when we are estimating the CareDriver to arrive. They may arrive early to prepare, but the ride will not start before your scheduled time.">
            <SvgBubble sx={{ marginLeft: '0.5rem' }}>
              <Svg icon="question" />
            </SvgBubble>
          </Tooltip>
        </Box>
      ),
      align: 'left',
      valueGetter: (rowData) => (
        <span aria-label={getEstimatedPickupTime(rowData, formatInTimezone) ? undefined : 'CareDriver Arrival Empty'}>
          {getEstimatedPickupTime(rowData, formatInTimezone)}
        </span>
      ),
    },

    pickupColumn,
    stopsColumn,
    dropoffColumn,
    tagsColumn,
    {
      label: 'Driver',
      align: 'left',
      valueGetter: (rowData) => getDriverName(rowData),
    },
    {
      label: 'Services',
      align: 'left',
      valueGetter: (rowData) => (
        <span aria-label={getRiderServices(rowData) ? undefined : 'Services Empty'}>{getRiderServices(rowData)}</span>
      ),
      minWidth: '167px',
    },
  ];
};

const completedRides = (formatInTimezone) => {
  return [
    statusColumn,
    passengersColumn(),
    {
      key: 'startsAt',
      label: 'Start Date / Time',
      align: 'left',
      valueSetter: (cellValue) => cellValue && formatInTimezone(cellValue, 'EEE, MM/dd, hh:mm aa'),
      minWidth: '167px',
    },
    pickupColumn,
    stopsColumn,
    dropoffColumn,
    tagsColumn,
    {
      key: 'fare',
      label: 'Fare',
      align: 'left',
      valueSetter: (cellValue: number) => (
        <span aria-label={getFare(cellValue) ? undefined : 'Fare Empty'}>{getFare(cellValue)}</span>
      ),
    },
    {
      label: 'Services',
      align: 'left',
      valueGetter: (rowData) => (
        <span aria-label={getRiderServices(rowData) ? undefined : 'Services Empty'}>{getRiderServices(rowData)}</span>
      ),
      minWidth: '167px',
    },
  ];
};

const canceledRides = (formatInTimezone) => {
  return [
    statusColumn,
    passengersColumn(),
    {
      key: 'startsAt',
      label: 'Start Date / Time',
      align: 'left',
      valueSetter: (cellValue) => cellValue && formatInTimezone(cellValue, 'EEE, MM/dd, hh:mm aa'),
      minWidth: '167px',
    },
    {
      key: 'canceledBy',
      label: 'Canceled By',
      align: 'left',
      minWidth: '115px',
      valueSetter: (cellValue) => <span aria-label={cellValue ? undefined : 'Canceled By Empty'}>{cellValue}</span>,
    },
    {
      key: 'cancellationReason',
      label: 'Reason',
      align: 'left',
      maxWidth: '200px',
      valueSetter: (value: string) => <OverflowText>{value}</OverflowText>,
    },
    {
      key: 'canceledAt',
      label: 'Cancel Date',
      align: 'left',
      valueSetter: (value) => value && formatInTimezone(value, 'EEE, MM/dd, hh:mm aa'),
      minWidth: '167px',
    },
    {
      key: 'fare',
      label: 'Fare',
      align: 'left',
      valueSetter: (cellValue: number) => (
        <span aria-label={getFare(cellValue) ? undefined : 'Fare Empty'}>{getFare(cellValue)}</span>
      ),
    },
    {
      label: 'Services',
      align: 'left',
      valueGetter: (rowData) => (
        <span aria-label={getRiderServices(rowData) ? undefined : 'Services Empty'}>{getRiderServices(rowData)}</span>
      ),
      minWidth: '167px',
    },
  ];
};

const tagsColumn = {
  label: 'Tags',
  align: 'left',
  maxWidth: '230px',
  valueGetter: ({ tags }) => <span aria-label={getTags(tags) ? undefined : 'Tags Empty'}>{getTags(tags)}</span>,
};

const stopsColumn = {
  key: 'stops',
  label: 'Stops',
  align: 'center',
};

const dropoffColumn = {
  key: 'dropoffLocation',
  label: 'Dropoff Location',
  align: 'left',
  maxWidth: '200px',
  valueSetter: (cellValue) => <Text noWrap>{cellValue.name ? cellValue.name : cellValue.address}</Text>,
};

const pickupColumn = {
  key: 'pickupLocation',
  label: 'Pickup Location',
  align: 'left',
  maxWidth: '200px',
  valueSetter: (cellValue) => <Text noWrap>{cellValue.name ? cellValue.name : cellValue.address}</Text>,
};

const statusColumn = {
  label: 'Status',
  align: 'left',
  valueGetter: (rowData) => getStatusLabel(rowData),
};

const passengersColumn = () => {
  return {
    label: 'Riders',
    align: 'left',
    maxWidth: '200px',
    valueGetter: ({ passengers, sharedBillingRole }) =>
      passengers && (
        <Box display="flex" alignItems="center">
          <Text noWrap>
            <span style={{ marginTop: 'auto', display: 'flex', alignItems: 'center' }}>
              {`${passengers.length}`}
              {getSharedBillingInfo(sharedBillingRole)}
            </span>
            <Passenger passengers={passengers} />
          </Text>
        </Box>
      ),
  };
};

const Passenger = ({ passengers }) => {
  return passengers.map((rider, index) => {
    const name = getFirstNameLastInitial(rider.fullName);

    return (
      <Text key={rider}>
        <StyledSpan riding={rider.riding}>{name}</StyledSpan>
        {rider.riding === false && <span style={{ color: colorPalette.red55 }}> (not riding)</span>}
        {index < passengers.length - 1 && ', '}
      </Text>
    );
  });
};

const getEstimatedPickupTime = (rowData, formatInTimezone) => {
  const { estimatedStartsAt, timelinessStatus } = rowData;

  if (estimatedStartsAt) {
    return (
      <Text textColor={timelinessStatus === 'late' ? 'warning-dark' : 'text'}>
        {formatInTimezone(estimatedStartsAt, 'hh:mm aa')}
      </Text>
    );
  }
  if (timelinessStatus === 'delayed') {
    return (
      <MuiTooltip title="The estimated pickup time will be displayed when known">
        <TooltipBubble>
          <Text textColor="warning-dark">Delayed</Text>
        </TooltipBubble>
      </MuiTooltip>
    );
  }

  return null;
};

const StyledSpan = styled.span<{ riding: boolean }>`
  color: ${(props) => (props.riding === false ? colorTokens.text.medium : 'inherit')};
`;

const getTags = (tags: Tag[]) => {
  if (!tags || tags.length === 0) {
    return null;
  }

  return (
    <div>
      {tags.map((tag) => (
        <SimpleLabel mr={2} mt={'2px'} mb={'2px'} key={uniqueId()} label={tag.name} />
      ))}
    </div>
  );
};

const getDriverName: React.FC<{ driver: Driver }> = ({ driver }) => {
  if (!driver) {
    return <Text noWrap>{'Confirmed'}</Text>;
  }
  const splitNames = getFirstNameLastInitial(driver.fullName).split(' ');

  return (
    <Box display="flex" gap="4px">
      <Text noWrap>{splitNames[0]} </Text>
      <Text>{splitNames[1]}</Text>
    </Box>
  );
};

const getRiderServices = (rowData) => {
  if (!rowData?.services || !Array.isArray(rowData.services)) {
    return null;
  }

  const validCodes = SERVICE_ICON_CODES;
  const services = rowData.services.filter((service) => validCodes.includes(service.code));

  if (services.length === 0) {
    return null;
  }

  return (
    <Box display="flex" gap="4px">
      <RiderServices services={services} />
    </Box>
  );
};

const getStatusLabel: React.FC<{ state: string }> = ({ state }) => {
  switch (true) {
    case includes(RIDE_STATES.inProgress, state):
      return <SimpleLabel label="In Progress" textColor="text" bgColor="light-green" />;
    case includes(RIDE_STATES.upcoming, state):
      return <SimpleLabel label="Upcoming" textColor="text" bgColor="light-blue" />;
    case includes(RIDE_STATES.completed, state):
      return <SimpleLabel ml={2} label="Completed" textColor="text" bgColor="light-purple" />;
    case includes(RIDE_STATES.canceled, state):
      return <SimpleLabel ml={2} label="Canceled" textColor="text" bgColor="light-orange" />;
    case includes(RIDE_STATES.requested, state):
      return <SimpleLabel label="Requested" textColor="text" bgColor="neutralVariant90" />;
    case includes(RIDE_STATES.paused, state):
      return <SimpleLabel label="Paused" textColor="text" bgColor="orange80" />;
    default:
      null;
  }

  return null;
};

const getFare = (fare: number) => {
  if (!fare || fare === null) {
    return null;
  }

  return <div data-testid="fare">{formatCurrency(fare / 100)}</div>;
};

const getSharedBillingInfo = (sharedBillingInfo) => {
  if (!sharedBillingInfo) {
    return null;
  }
  const toolTipTitle =
    sharedBillingInfo === 'Attending' ? 'Shared - Attending District' : 'Shared - Residence District';

  return (
    <Box
      sx={{ bgcolor: colorTokens.info.light, p: '0 2px', ml: '5px', borderRadius: '2px' }}
      display="flex"
      flexDirection="column"
      justifyContent="center"
      height="16px"
      data-testid="shared-billing-info"
    >
      <MuiTooltip title={toolTipTitle}>
        <TooltipBubble>
          {sharedBillingInfo === 'Attending' && <Text textColor="white">SA</Text>}
          {sharedBillingInfo === 'Residence' && <Text textColor="white">SR</Text>}
        </TooltipBubble>
      </MuiTooltip>
    </Box>
  );
};

const TooltipBubble = styled.div`
  .MuiTooltip-popper {
    border-radius: 50%;
  }
`;
